// src/app/core/shared/t3-model/t3-baseline.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

import { T3AnalysisStore } from './t3-analysis.store';
import { T3ApiResponse } from './t3-response-model';

const MODEL = 't3';

@Injectable({ providedIn: 'root' })
export class T3BaselineService {
    private readonly baselineUrl = `${environment.apiUrl}/city_baseline`;

    private readonly headers = new HttpHeaders({
        Authorization: `Bearer ${environment.handshakeToken}`
    });

    constructor(
        private http: HttpClient,
        private store: T3AnalysisStore
    ) { }

    async resetAndReloadBaselineForCity(city: string): Promise<void> {
        const sessionId = this.ensureSessionId();

        if (!city) {
            throw new Error('Missing selected city.');
        }

        const t3 = await this.fetchBaseline(city, sessionId);

        this.store.setBaselineRun(t3);
    }

    private async fetchBaseline(city: string, sessionId: string): Promise<T3ApiResponse> {
        const params = new HttpParams()
            .set('model', MODEL)
            .set('city', city)
            .set('session_id', sessionId);

        const text = await firstValueFrom(
            this.http.get(this.baselineUrl, {
                params,
                headers: this.headers,
                responseType: 'text'
            })
        );

        const decoded = this.decodeB64Json<any>(text);
        const t3 = this.extractT3Response(decoded);

        if (!t3 || !Array.isArray(t3.years)) {
            console.error('[T3BaselineService] Unexpected baseline response:', decoded);
            throw new Error(decoded?.message || 'T3 baseline request failed.');
        }

        return t3;
    }

    private extractT3Response(response: any): T3ApiResponse | null {
        if (!response) {
            return null;
        }

        const payload =
            response?.htn_output ??
            response?.data?.htn_output ??
            response?.response?.htn_output ??
            response?.data ??
            response?.response ??
            response;

        if (!payload || !Array.isArray(payload?.years)) {
            return null;
        }

        return {
            ...payload,
            status: response?.status ?? payload?.status ?? 'success',
            city_name: response?.city_name ?? payload?.city_name,
            id: response?.id,
            session: response?.session,
            intervention_names: response?.intervention_names,
            parameter_details: response?.parameter_details
        } as T3ApiResponse;
    }

    private decodeB64Json<T>(value: string): T {
        const raw = String(value || '').trim();

        try {
            const json = atob(raw);
            return JSON.parse(json) as T;
        } catch {
            return JSON.parse(raw) as T;
        }
    }

    private ensureSessionId(): string {
        const key = 'session_id';

        let id =
            sessionStorage.getItem(key) ||
            localStorage.getItem(key) ||
            '';

        if (!id) {
            id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        }

        try {
            sessionStorage.setItem(key, id);
            localStorage.setItem(key, id);
        } catch { }

        return id;
    }
}